import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import {
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  MultiSelectCombobox,
  useAuth,
} from '@aaas/shared';
import jobService from '../services/job.service';
import { usePositionListQuery } from '../hooks/usePositionQueries';
import { useCampaignListQuery } from '../hooks/useCampaignQueries';
import { usePipelineListQuery } from '../hooks/usePipelineQueries';
import { useEmployeeTypeListQuery } from '../hooks/useEmployeeTypeQueries';
import { useRecruitmentTypeListQuery } from '../hooks/useRecruitmentTypeQueries';
import { useWorkArrangementListQuery } from '../hooks/useWorkArrangementQueries';
import { useRecruiterListQuery } from '../hooks/useMemberRoleQueries';
import { PipelineChangeWarningDialog } from './PipelineChangeWarningDialog';
import { PositionFormDialog } from './PositionFormDialog';
import { CampaignFormDialog } from './CampaignFormDialog';
import type { Job, JobCreateInput, JobUpdateInput, JobStatus } from '../types';

// Searchable Combobox component for form fields
interface SearchableSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  error?: boolean;
}

function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  allowEmpty = false,
  emptyLabel,
  error = false,
}: SearchableSelectProps) {
  const { t } = useTranslation('ats');
  const sp = searchPlaceholder ?? t('job.form.searchPlaceholder');
  const em = emptyMessage ?? t('job.form.noResults');
  const el = emptyLabel ?? t('job.form.noneOption');
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            disabled && 'opacity-50 cursor-not-allowed',
            error && 'border-red-500 focus-visible:ring-red-500'
          )}
        >
          <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={sp} />
          <CommandList>
            <CommandEmpty>{em}</CommandEmpty>
            <CommandGroup>
              {allowEmpty && (
                <CommandItem
                  value=""
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                  {el}
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface JobFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  job?: Job | null;
  onSuccess: () => void;
}

export function JobFormDialog({ open, onClose, mode, job, onSuccess }: JobFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace, idToken } = useAuth();

  // Form state
  const [code, setCode] = useState('');
  const [positionId, setPositionId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [employeeTypeId, setEmployeeTypeId] = useState<string | null>(null);
  const [recruitmentTypeId, setRecruitmentTypeId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [status, setStatus] = useState<JobStatus>('open');
  const [workArrangementId, setWorkArrangementId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [recruiterSearch, setRecruiterSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Pipeline change warning state
  const [showPipelineWarning, setShowPipelineWarning] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<JobUpdateInput | null>(null);

  // Position creation dialog state
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const [, setPendingPositionId] = useState<string | null>(null);

  // Campaign creation dialog state
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);

  // Fetch existing job assignments when editing
  const [existingAssignments, setExistingAssignments] = useState<string[]>([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // Fetch dropdown options
  const { data: positionsData, refetch: refetchPositions } = usePositionListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: open && !!currentWorkspace,
    refetchInterval: false,
  });

  const { data: campaignsData, refetch: refetchCampaigns } = useCampaignListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: open && !!currentWorkspace,
    refetchInterval: false,
  });

  const { data: pipelinesData } = usePipelineListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: open && !!currentWorkspace,
    refetchInterval: false,
  });

  const { data: employeeTypesData } = useEmployeeTypeListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: open && !!currentWorkspace,
    refetchInterval: false,
  });

  const { data: recruitmentTypesData } = useRecruitmentTypeListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: open && !!currentWorkspace,
    refetchInterval: false,
  });

  const { data: workArrangementsData } = useWorkArrangementListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: open && !!currentWorkspace,
    refetchInterval: false,
  });

  const { data: recruitersData } = useRecruiterListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    search: recruiterSearch,
    enabled: open && !!currentWorkspace,
  });

  const positions = positionsData?.data?.items || [];
  const campaigns = campaignsData?.data?.items || [];
  const pipelines = pipelinesData?.data?.items || [];
  const employeeTypes = employeeTypesData?.data?.items || [];
  const recruitmentTypes = recruitmentTypesData?.data?.items || [];
  const workArrangements = workArrangementsData?.data?.items || [];
  const recruiters = recruitersData || [];
  // Get new pipeline info for warning dialog
  const newPipelineInfo = useMemo(() => {
    if (!pipelineId) return null;
    const newPipeline = pipelines.find((p) => p.id === pipelineId);
    if (!newPipeline) return null;

    // Sort rounds by order and get first
    const sortedRounds = [...newPipeline.rounds].sort((a, b) => a.order - b.order);
    const firstRound = sortedRounds[0];

    return {
      pipelineName: newPipeline.name,
      firstRoundName: firstRound?.name || t('job.form.fallbackRoundName'),
    };
  }, [pipelineId, pipelines]);

  // Reset form when dialog opens or job changes
  useEffect(() => {
    const loadJobData = async () => {
      if (open) {
        if (mode === 'edit' && job) {
          setCode(job.code);
          setPositionId(job.position.id);
          setCampaignId(job.campaign.id);
          setPipelineId(job.pipeline?.id || null);
          setEmployeeTypeId(job.employee_type?.id || null);
          setRecruitmentTypeId(job.recruitment_type?.id || null);
          setQuantity(job.quantity);
          setStatus(job.status);
          setWorkArrangementId(job.work_arrangement?.id || null);
          setStartDate(job.start_date || '');
          setEndDate(job.end_date || '');
          setNote(job.note || '');

          // Fetch existing assignments for edit mode
          if (user && currentWorkspace) {
            setIsLoadingAssignments(true);
            try {
              const token = await user.getIdToken();
              const assignmentsData = await jobService.listJobAssignments(
                job.id,
                currentWorkspace.id,
                token
              );
              const assigneeIdsList = assignmentsData.items.map((a) => a.assignee.id);
              setAssigneeIds(assigneeIdsList);
              setExistingAssignments(assigneeIdsList);
            } catch (err) {
              console.error('Failed to load job assignments:', err);
              setAssigneeIds([]);
              setExistingAssignments([]);
            } finally {
              setIsLoadingAssignments(false);
            }
          }
        } else {
          setCode('');
          setPositionId('');
          setCampaignId('');
          setPipelineId(null);
          setEmployeeTypeId(null);
          setRecruitmentTypeId(null);
          setQuantity(1);
          setStatus('open');
          setWorkArrangementId(null);
          setStartDate('');
          setEndDate('');
          setNote('');
          setAssigneeIds([]);
          setExistingAssignments([]);
        }
        setError(null);
        setFieldErrors({});
        setShowPipelineWarning(false);
        setPendingSubmitData(null);
        setShowPositionDialog(false);
        setPendingPositionId(null);
        setShowCampaignDialog(false);
      }
    };

    loadJobData();
  }, [open, mode, job, user, currentWorkspace]);

  // Build form data for update
  // Note: assignee_ids are handled separately via assignment endpoints in edit mode
  const buildUpdateData = (): JobUpdateInput => ({
    code: code.trim(),
    position_id: positionId,
    campaign_id: campaignId,
    pipeline_id: pipelineId,
    employee_type_id: employeeTypeId,
    recruitment_type_id: recruitmentTypeId,
    work_arrangement_id: workArrangementId,
    quantity,
    status,
    start_date: startDate || null,
    end_date: endDate || null,
    note: note.trim() || null,
  });

  // Submit job (create or update)
  const submitJob = async (moveCvsOnPipelineChange: boolean = false) => {
    if (!user || !currentWorkspace) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: JobCreateInput = {
          code: code.trim(),
          position_id: positionId,
          campaign_id: campaignId,
          pipeline_id: pipelineId!, // validated above
          employee_type_id: employeeTypeId,
          recruitment_type_id: recruitmentTypeId,
          work_arrangement_id: workArrangementId,
          quantity,
          status,
          start_date: startDate || null,
          end_date: endDate || null,
          note: note.trim() || null,
          assignee_ids: assigneeIds.length > 0 ? assigneeIds : null,
        };
        await jobService.createJob(currentWorkspace.id, token, data);
      } else if (job) {
        const data = pendingSubmitData || buildUpdateData();
        await jobService.updateJob(job.id, currentWorkspace.id, token, data, {
          moveCvsOnPipelineChange,
        });

        // Sync job assignments in edit mode
        // Calculate which assignees to add and remove
        const toAdd = assigneeIds.filter((id) => !existingAssignments.includes(id));
        const toRemove = existingAssignments.filter((id) => !assigneeIds.includes(id));

        // Add new assignments
        if (toAdd.length > 0) {
          await jobService.createJobAssignmentsBulk(job.id, currentWorkspace.id, token, {
            assignee_ids: toAdd,
          });
        }

        // Remove unselected assignments
        for (const assigneeId of toRemove) {
          try {
            await jobService.deleteJobAssignment(job.id, assigneeId, currentWorkspace.id, token);
          } catch (err) {
            console.error(`Failed to remove assignment for ${assigneeId}:`, err);
          }
        }
      }

      setShowPipelineWarning(false);
      setPendingSubmitData(null);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} job:`, err);

      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail;

        // Case 1: String error - parse for field hints
        if (typeof detail === 'string') {
          // Duplicate code error pattern
          if (detail.toLowerCase().includes('code') && detail.includes('already exists')) {
            setFieldErrors({ code: detail });
            return;
          }
          // Generic error
          setError(detail);
          return;
        }

        // Case 2: Pydantic validation errors array
        if (Array.isArray(detail)) {
          const fieldErrs: Record<string, string> = {};
          for (const e of detail) {
            const field = e.loc?.[1]; // ["body", "field_name"]
            if (field) fieldErrs[field] = e.msg;
          }
          if (Object.keys(fieldErrs).length > 0) {
            setFieldErrors(fieldErrs);
            return;
          }
        }
      }

      // Fallback
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle confirm pipeline change
  const handleConfirmPipelineChange = () => {
    submitJob(true); // moveCvsOnPipelineChange = true
  };

  // Handle cancel pipeline change warning
  const handleCancelPipelineWarning = () => {
    setShowPipelineWarning(false);
    setPendingSubmitData(null);
  };

  // Handle position creation success
  const handlePositionCreated = async () => {
    // Refetch positions to include the newly created one
    const result = await refetchPositions();

    // Get the latest position (assuming it's the first one after refetch)
    const latestPosition = result.data?.data?.items?.[0];

    if (latestPosition) {
      // Set the newly created position as selected
      setPositionId(latestPosition.id);

      // Clear field error if any
      if (fieldErrors.position_id) {
        setFieldErrors((prev) => ({ ...prev, position_id: '' }));
      }
    }
  };

  // Handle campaign creation success
  const handleCampaignCreated = async () => {
    // Refetch campaigns to include the newly created one
    const result = await refetchCampaigns();

    // Get the latest campaign (assuming it's the first one after refetch)
    const latestCampaign = result.data?.data?.items?.[0];

    if (latestCampaign) {
      // Set the newly created campaign as selected
      setCampaignId(latestCampaign.id);

      // Clear field error if any
      if (fieldErrors.campaign_id) {
        setFieldErrors((prev) => ({ ...prev, campaign_id: '' }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;

    // Validate required fields
    const errors: Record<string, string> = {};
    if (!code.trim()) {
      errors.code = t('job.form.errors.codeRequired');
    }
    if (!positionId) {
      errors.position_id = t('job.form.errors.positionRequired');
    }
    if (!campaignId) {
      errors.campaign_id = t('job.form.errors.campaignRequired');
    }
    if (!pipelineId) {
      errors.pipeline_id = t('job.form.errors.pipelineRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    // Check if pipeline changed and job has CVs (edit mode only)
    if (mode === 'edit' && job) {
      const pipelineChanged = pipelineId !== job.pipeline?.id;
      const hasCVs = (job.cv_count ?? 0) > 0;

      if (pipelineChanged && hasCVs) {
        // Store form data and show warning
        setPendingSubmitData(buildUpdateData());
        setShowPipelineWarning(true);
        return;
      }
    }

    // Normal submit (no pipeline change or no CVs)
    await submitJob(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? t('job.form.createTitle') : t('job.form.editTitle')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Code, Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('job.form.codeLabel')}</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (fieldErrors.code) setFieldErrors((prev) => ({ ...prev, code: '' }));
                  }}
                  placeholder={t('job.form.codePlaceholder')}
                  disabled={isSubmitting}
                  autoFocus
                  className={cn(fieldErrors.code && 'border-red-500 focus-visible:ring-red-500')}
                />
                {fieldErrors.code && <p className="text-sm text-red-500">{fieldErrors.code}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">{t('job.form.statusLabel')}</Label>
                <Select value={status} onValueChange={(value) => setStatus(value as JobStatus)}>
                  <SelectTrigger className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}>
                    <SelectValue placeholder={t('job.form.statusPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">{t('job.form.statusOpen')}</SelectItem>
                    <SelectItem value="closed">{t('job.form.statusClosed')}</SelectItem>
                    <SelectItem value="on_hold">{t('job.form.statusOnHold')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Position, Campaign */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="position">{t('job.form.positionLabel')}</Label>
                  <button
                    type="button"
                    onClick={() => setShowPositionDialog(true)}
                    className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
                    disabled={isSubmitting}
                  >
                    <Plus className="h-3 w-3" />
                    {t('job.form.addNew')}
                  </button>
                </div>
                <SearchableSelect
                  options={positions.map((pos) => ({ value: pos.id, label: pos.name }))}
                  value={positionId}
                  onChange={(value) => {
                    setPositionId(value);
                    if (fieldErrors.position_id)
                      setFieldErrors((prev) => ({ ...prev, position_id: '' }));
                  }}
                  placeholder={t('job.form.positionPlaceholder')}
                  searchPlaceholder={t('job.form.searchPositions')}
                  emptyMessage={t('job.form.noPositions')}
                  disabled={isSubmitting}
                  error={!!fieldErrors.position_id}
                />
                {fieldErrors.position_id && (
                  <p className="text-sm text-red-500">{fieldErrors.position_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="campaign">{t('job.form.campaignLabel')}</Label>
                  <button
                    type="button"
                    onClick={() => setShowCampaignDialog(true)}
                    className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
                    disabled={isSubmitting}
                  >
                    <Plus className="h-3 w-3" />
                    {t('job.form.addNew')}
                  </button>
                </div>
                <SearchableSelect
                  options={campaigns.map((camp) => ({ value: camp.id, label: camp.name }))}
                  value={campaignId}
                  onChange={(value) => {
                    setCampaignId(value);
                    if (fieldErrors.campaign_id)
                      setFieldErrors((prev) => ({ ...prev, campaign_id: '' }));
                  }}
                  placeholder={t('job.form.campaignPlaceholder')}
                  searchPlaceholder={t('job.form.searchCampaigns')}
                  emptyMessage={t('job.form.noCampaigns')}
                  disabled={isSubmitting}
                  error={!!fieldErrors.campaign_id}
                />
                {fieldErrors.campaign_id && (
                  <p className="text-sm text-red-500">{fieldErrors.campaign_id}</p>
                )}
              </div>
            </div>

            {/* Row 3: Pipeline, Quantity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pipeline">{t('job.form.pipelineLabel')}</Label>
                <SearchableSelect
                  options={pipelines.map((pl) => ({ value: pl.id, label: pl.name }))}
                  value={pipelineId || ''}
                  onChange={(value) => {
                    setPipelineId(value || null);
                    if (fieldErrors.pipeline_id)
                      setFieldErrors((prev) => ({ ...prev, pipeline_id: '' }));
                  }}
                  placeholder={t('job.form.pipelinePlaceholder')}
                  searchPlaceholder={t('job.form.searchPipelines')}
                  emptyMessage={t('job.form.noPipelines')}
                  disabled={isSubmitting}
                  error={!!fieldErrors.pipeline_id}
                />
                {fieldErrors.pipeline_id && (
                  <p className="text-sm text-red-500">{fieldErrors.pipeline_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">{t('job.form.quantityLabel')}</Label>
                <Input
                  id="quantity"
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Row 4: Employee Type, Recruitment Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="employeeType">{t('job.form.employeeTypeLabel')}</Label>
                <SearchableSelect
                  options={employeeTypes.map((et) => ({ value: et.id, label: et.name }))}
                  value={employeeTypeId || ''}
                  onChange={(value) => setEmployeeTypeId(value || null)}
                  placeholder={t('job.form.employeeTypePlaceholder')}
                  searchPlaceholder={t('job.form.searchEmployeeTypes')}
                  emptyMessage={t('job.form.noEmployeeTypes')}
                  disabled={isSubmitting}
                  allowEmpty
                  emptyLabel={t('job.form.noneOption')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="recruitmentType">{t('job.form.recruitmentTypeLabel')}</Label>
                <SearchableSelect
                  options={recruitmentTypes.map((rt) => ({ value: rt.id, label: rt.name }))}
                  value={recruitmentTypeId || ''}
                  onChange={(value) => setRecruitmentTypeId(value || null)}
                  placeholder={t('job.form.recruitmentTypePlaceholder')}
                  searchPlaceholder={t('job.form.searchRecruitmentTypes')}
                  emptyMessage={t('job.form.noRecruitmentTypes')}
                  disabled={isSubmitting}
                  allowEmpty
                  emptyLabel={t('job.form.noneOption')}
                />
              </div>
            </div>

            {/* Row 5: Work Arrangement, Assignees */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="workArrangement">{t('job.form.workArrangementLabel')}</Label>
                <SearchableSelect
                  options={workArrangements.map((wa) => ({ value: wa.id, label: wa.name }))}
                  value={workArrangementId || ''}
                  onChange={(value) => setWorkArrangementId(value || null)}
                  placeholder={t('job.form.workArrangementPlaceholder')}
                  searchPlaceholder={t('job.form.searchWorkArrangements')}
                  emptyMessage={t('job.form.noWorkArrangements')}
                  disabled={isSubmitting}
                  allowEmpty
                  emptyLabel={t('job.form.noneOption')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignees">{t('job.form.assigneesLabel')}</Label>
                <MultiSelectCombobox
                  options={recruiters.map((member) => ({
                    value: member.account_id,
                    label: member.account_name,
                  }))}
                  values={assigneeIds}
                  onChange={setAssigneeIds}
                  onSearchChange={setRecruiterSearch}
                  placeholder={t('job.form.assigneesPlaceholder')}
                  displayName={t('job.form.assigneesName')}
                  searchPlaceholder={t('job.form.searchRecruiters')}
                  emptyMessage={t('job.form.noRecruiters')}
                  isLoading={isLoadingAssignments}
                  className={isSubmitting || isLoadingAssignments ? 'opacity-50 cursor-not-allowed w-full' : 'w-full'}
                />
              </div>
            </div>

            {/* Row 6: Start Date, End Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">{t('job.form.startDateLabel')}</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">{t('job.form.endDateLabel')}</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="note">{t('job.form.noteLabel')}</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('job.form.notePlaceholder')}
                disabled={isSubmitting}
                rows={3}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                {t('config.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {mode === 'create' ? t('common.creating') : t('common.saving')}
                  </>
                ) : mode === 'create' ? (
                  t('job.form.createButton')
                ) : (
                  t('common.saveChanges')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pipeline Change Warning Dialog */}
      {newPipelineInfo && (
        <PipelineChangeWarningDialog
          open={showPipelineWarning}
          cvCount={job?.cv_count ?? 0}
          newPipelineName={newPipelineInfo.pipelineName}
          firstRoundName={newPipelineInfo.firstRoundName}
          onConfirm={handleConfirmPipelineChange}
          onCancel={handleCancelPipelineWarning}
          isLoading={isSubmitting}
        />
      )}

      {/* Position Creation Dialog */}
      <PositionFormDialog
        open={showPositionDialog}
        onClose={() => setShowPositionDialog(false)}
        mode="create"
        position={null}
        onSuccess={handlePositionCreated}
      />

      {/* Campaign Creation Dialog */}
      <CampaignFormDialog
        open={showCampaignDialog}
        onClose={() => setShowCampaignDialog(false)}
        mode="create"
        campaign={null}
        onSuccess={handleCampaignCreated}
      />
    </>
  );
}
